import { Box, Button, Grow, Paper, Popover, Stack, Typography } from '@mui/material';

import ReferralHeroText from './ReferralHeroText';

import CloseIcon from 'modules/common/components/Icons/CloseIcon';
import useReferralPromoConfig from 'modules/common/hooks/useReferralPromoConfig';

/** Spacing units (default 8px each) — hero strip ≈115px tall. */
const REFERRAL_PROMPT_HERO_HEIGHT_UNITS = 14.375;
/** Spacing units — card max width ≈294px. */
const REFERRAL_PROMPT_CARD_MAX_WIDTH_UNITS = 36.75;
/** Fits “Copied!” + horizontal padding without layout shift when label toggles. */
const COPY_BUTTON_WIDTH_UNITS = 11;

/** Display host + path only (matches referral UIs that omit the scheme). */
function formatShareUrlForDisplay(url: string | null): string {
    if (!url) {
        return '';
    }
    return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

interface ReferralPromptViewProps {
    open: boolean;
    rewardText: string;
    /** Resolved share URL to display next to Copy (may load after mount). */
    shareUrl: string | null;
    shareUrlLoading: boolean;
    /** After a successful copy, the copy control shows this feedback instead of changing step. */
    linkCopied: boolean;
    onClose: () => void;
    onReferNow: () => void;
}

export default function ReferralPromptView({
    open,
    rewardText,
    shareUrl,
    shareUrlLoading,
    linkCopied,
    onClose,
    onReferNow,
}: ReferralPromptViewProps) {
    const { deadlineDisplay, discountLabel } = useReferralPromoConfig();
    const displayLink = formatShareUrlForDisplay(shareUrl);
    const linkStatusText = shareUrlLoading ? 'Loading link…' : displayLink || 'Link unavailable';
    const copyDisabled = shareUrlLoading || !shareUrl;

    return (
        <Popover
            slotProps={{
                paper: {
                    sx: { minWidth: '0px', minHeight: '0px', zIndex: (theme) => theme.zIndex.drawer + 10 },
                },
            }}
            disableRestoreFocus
            hideBackdrop
            // Click-through (see `pointerEvents` below), so it must not trap focus either.
            disableEnforceFocus
            open={open}
            sx={{ pointerEvents: 'none' }}>
            <Grow in={open} timeout={1000} appear>
                <Paper
                    elevation={24}
                    sx={{
                        boxShadow: (theme) => theme.shadows[24],
                        pointerEvents: 'auto',
                        position: 'fixed',
                        bottom: { xs: 2, sm: 4 },
                        left: { xs: 2, sm: 'auto' },
                        right: { xs: 2, sm: 4 },
                        maxWidth: (theme) => theme.spacing(REFERRAL_PROMPT_CARD_MAX_WIDTH_UNITS),
                        borderRadius: 4,
                        m: 2,
                        overflow: 'hidden',
                        p: 0,
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}>
                    <Box
                        sx={{
                            alignItems: 'center',
                            bgcolor: 'purple.800',
                            display: 'flex',
                            height: (theme) => theme.spacing(REFERRAL_PROMPT_HERO_HEIGHT_UNITS),
                            justifyContent: 'center',
                            lineHeight: 0,
                            overflow: 'hidden',
                            position: 'relative',
                            width: '100%',
                        }}>
                        <ReferralHeroText rewardText={rewardText} />
                        <CloseIcon
                            onClick={onClose}
                            size="small"
                            sx={{
                                backgroundColor: 'common.white',
                                color: 'cement.600',
                                position: 'absolute',
                                right: (theme) => theme.spacing(1),
                                top: (theme) => theme.spacing(1),
                            }}
                        />
                    </Box>
                    <Stack spacing={1.5} sx={{ p: 2 }}>
                        {deadlineDisplay && (
                            <Box alignSelf="flex-start" bgcolor="grey.50" borderRadius={1} px={1.25} py={0.5}>
                                <Typography variant="overline" color="text.primary">
                                    Ends {deadlineDisplay}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body1" color="text.primary">
                            <Typography component="span" variant="body1" fontWeight="bold">
                                Invite a friend—they get {discountLabel} off any plan.{' '}
                            </Typography>
                            You earn up to {rewardText} per referral.
                        </Typography>
                        <Stack spacing={0.5} sx={{ alignSelf: 'stretch', width: '100%' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                component="label"
                                htmlFor="referral-prompt-share-link">
                                Your referral link
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="stretch">
                                <Box
                                    id="referral-prompt-share-link"
                                    aria-readonly="true"
                                    role="textbox"
                                    sx={{
                                        alignItems: 'center',
                                        bgcolor: 'cement.100',
                                        borderRadius: (theme) => theme.spacing(1.5),
                                        display: 'flex',
                                        flex: 1,
                                        minHeight: (theme) => theme.spacing(4.5),
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        px: 1.5,
                                        py: 1,
                                    }}
                                    title={shareUrl ?? undefined}>
                                    <Typography
                                        variant="caption"
                                        color="text.primary"
                                        fontWeight={600}
                                        noWrap
                                        sx={{ minWidth: 0, width: '100%' }}>
                                        {linkStatusText}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    color={linkCopied ? 'success' : 'primary'}
                                    size="small"
                                    disabled={copyDisabled}
                                    onClick={onReferNow}
                                    sx={{
                                        alignSelf: 'stretch',
                                        flexShrink: 0,
                                        fontWeight: 'bold',
                                        width: (theme) => theme.spacing(COPY_BUTTON_WIDTH_UNITS),
                                        minWidth: (theme) => theme.spacing(COPY_BUTTON_WIDTH_UNITS),
                                        px: 1,
                                    }}>
                                    {linkCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
            </Grow>
        </Popover>
    );
}
